import { useContext, useRef, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import set from 'lodash.set';
import unset from 'lodash.unset';
import axios from 'axios';
import get from 'lodash.get';

import type { DependenciesFormValues, FormValues } from '../types/FormValues';
import type { FieldQuery, FieldQueryData } from '../context/types/store';
import {
  isDebounceCancelError,
  useDebouncedCallbackWithPromise,
} from '../context/utils/useDebouncedCallbackWithPromise';
import { getDependenciesValues } from '../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../context/utils/getIsDependenciesSatisfied';
import { getFieldQueryByField } from '../context/utils/getFieldQueryByField';
import type { TouchedValues } from '../context/ContestManageContext';
import { ContestManageContext } from '../context/ContestManageContext';
import {
  createRemoveFieldCacheAction,
  createSetFieldCacheAction,
  createUpdateFieldAction,
  createUpdateFieldCacheAction,
} from '../context/store/store.actions';
import { useDebouncedEffect } from '~/domains/common/utils/useDebouncedEffect';
import { getDependencyFlags } from '../context/utils/getDependencyFlags';
import { checkDependencyChanged } from '../context/utils/checkDependencyChanged';
import type { RefetchWhenDescriptor, FieldDependsOnDescriptor } from '../fields/types/common';

// =============================================================================
// refetchWhen Helpers
// @see fields/types/DEPENDENCY_SYSTEM_README.md for full documentation
// =============================================================================

/**
 * Evaluates a condition descriptor (refetchWhen).
 * @returns true if ALL conditions are met
 */
const evaluateConditionDescriptor = (
  descriptor: RefetchWhenDescriptor,
  dependencyValues: DependenciesFormValues
): boolean => {
  const path = descriptor.sectionKey
    ? [descriptor.sectionKey, ...descriptor.fieldKey.split('.')]
    : descriptor.fieldKey.split('.');

  const value = get(dependencyValues, path);

  return Object.entries(descriptor.conditions).every(([operator, expected]) => {
    switch (operator) {
      case '$eq':
        return value === expected;
      case '$neq':
        return value !== expected;
      default:
        return true;
    }
  });
};

/**
 * Checks if refetch should be skipped based on `refetchWhen`.
 * @returns true if dependency changed but refetchWhen condition is NOT met (skip refetch)
 */
const shouldSkipRefetch = (
  dependsOn: FieldDependsOnDescriptor[] | undefined,
  prevDependencyValues: DependenciesFormValues,
  currentDependencyValues: DependenciesFormValues,
  sectionKey: string
): boolean => {
  if (!dependsOn?.length) return false;

  // Check each dependency that changed
  for (const dependency of dependsOn) {
    const changed = checkDependencyChanged(
      dependency,
      prevDependencyValues,
      currentDependencyValues,
      sectionKey
    );

    // If this changed dependency has refetchWhen and condition is NOT met, skip refetch
    if (changed && 'refetchWhen' in dependency && dependency.refetchWhen) {
      const shouldRefetch = evaluateConditionDescriptor(
        dependency.refetchWhen,
        currentDependencyValues
      );
      if (!shouldRefetch) {
        return true; // Skip refetch - this dependency changed but doesn't require refetch
      }
    }
  }

  return false;
};

type QueryFn = (
  newQueryKey: string,
  payload: FormValues,
  abortController: AbortController,
  currentFieldQuery: FieldQueryData | null
) => Promise<any>;

export const useEnsureFieldData = ({
  field,
  fieldKey,
  sectionKey,
  queryFn,
  setTouched,
  setValues,
  path,
}: {
  field: FieldQuery;
  fieldKey: string;
  sectionKey: string;
  queryFn: QueryFn;
  path?: string[];
  setValues: (fn: (prev: FormValues) => FormValues) => void;
  setTouched: (fn: (prev: TouchedValues) => TouchedValues) => void;
}) => {
  const { store, dispatchStoreAction, values, errors } = useContext(ContestManageContext);
  const abortControllerRef = useRef<Record<string, AbortController>>({});
  const [retryFn, setRetryFn] = useState<() => void>(null);
  const storeRef = useRef(store);
  const prevDependencyValuesRef = useRef<DependenciesFormValues>({});

  const prevDependencyValues = prevDependencyValuesRef.current;
  storeRef.current = store;

  const debouncedQueryFn = useDebouncedCallbackWithPromise(queryFn, 500);

  useDebouncedEffect(
    () => {
      if (!field || (field.type !== 'DynamicField' && field.type !== 'DynamicFields')) {
        return;
      }

      const dependencyValues = getDependenciesValues(values, sectionKey, field.dependsOn);
      prevDependencyValuesRef.current = dependencyValues;

      const isDependenciesSatisfied = getIsDependenciesSatisfied(
        sectionKey,
        field.dependsOn,
        errors,
        dependencyValues
      );

      if (!isDependenciesSatisfied) {
        if (!field.latestQueryKey) {
          return;
        }
        dispatchStoreAction(createUpdateFieldAction(fieldKey, { latestQueryKey: undefined }));
        return;
      }

      const newFieldQueryKey = `${field.endpointName}:${JSON.stringify(dependencyValues)}`;
      const currentFieldQuery = getFieldQueryByField(storeRef.current, field);
      if (
        field.latestQueryKey === newFieldQueryKey &&
        (currentFieldQuery.isLoading || currentFieldQuery?.data || currentFieldQuery?.error)
      ) {
        return;
      }

      // Check if refetch should be skipped based on refetchWhen conditions
      // If a dependency changed but its refetchWhen condition is not met, don't refetch
      const skipRefetch = shouldSkipRefetch(
        field.dependsOn,
        prevDependencyValues,
        dependencyValues,
        sectionKey
      );
      if (skipRefetch && currentFieldQuery?.data) {
        // Don't refetch, keep using the existing data
        return;
      }

      dispatchStoreAction(createUpdateFieldAction(fieldKey, { latestQueryKey: newFieldQueryKey }));

      if (!storeRef.current.fieldsCache[newFieldQueryKey]) {
        const prevQueryAbortController = abortControllerRef.current[field.latestQueryKey];
        if (prevQueryAbortController) {
          // FIXME: Temporary fix for aborting requests so all other reducer actions can be dispatched before cancellation
          setTimeout(() => {
            prevQueryAbortController.abort();
          }, 1);
        }
        const newAbortController = new AbortController();
        abortControllerRef.current[newFieldQueryKey] = newAbortController;

        const payload = dependencyValues;

        let retries = 0;
        const callQueryFn = () => {
          dispatchStoreAction(
            createSetFieldCacheAction(newFieldQueryKey, {
              key: field.key,
              type: field.type,
              isLoading: true,
            })
          );

          return debouncedQueryFn(
            newFieldQueryKey,
            payload,
            newAbortController,
            currentFieldQuery
          ).catch((error) => {
            if (axios.isCancel(error) || isDebounceCancelError(error)) {
              dispatchStoreAction(createRemoveFieldCacheAction(newFieldQueryKey));
              return;
            }

            if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
              if (retries < 2) {
                retries += 1;

                return new Promise((resolve) => {
                  setTimeout(() => {
                    resolve(callQueryFn());
                  }, 1000);
                });
              }
              retries = 0;
            }
            if (isDebounceCancelError(error)) {
              dispatchStoreAction(createRemoveFieldCacheAction(newFieldQueryKey));
              return;
            }

            dispatchStoreAction(
              createUpdateFieldCacheAction(newFieldQueryKey, {
                isLoading: false,
                error,
              })
            );
            setRetryFn(() => () => {
              callQueryFn();
            });
          });
        };

        callQueryFn();
      }
      if (currentFieldQuery.type === 'DynamicFields' || !currentFieldQuery.data) {
        return;
      }

      setValues((prev) => {
        const currentValue = get(prev, [sectionKey, ...path, currentFieldQuery.data.key]);

        if (typeof currentValue === 'undefined') {
          return prev;
        }

        const newPrev = cloneDeep(prev);

        const { preserveOnChange, softReset } = getDependencyFlags(
          prevDependencyValues,
          dependencyValues,
          sectionKey,
          field.dependsOn
        );

        if (preserveOnChange) {
          set(newPrev, [sectionKey, ...path, currentFieldQuery.data.key], currentValue);
        } else if (softReset && 'defaultValue' in currentFieldQuery.data) {
          set(
            newPrev,
            [sectionKey, ...path, currentFieldQuery.data.key],
            currentFieldQuery.data.defaultValue
          );
        } else {
          unset(newPrev, [sectionKey, ...path, currentFieldQuery.data.key]);
        }

        return newPrev;
      });
      setTouched((prev) => {
        if (typeof get(prev, [sectionKey, ...path, currentFieldQuery.data.key]) === 'undefined') {
          return prev;
        }

        const newPrev = cloneDeep(prev);
        unset(newPrev, [sectionKey, ...path, currentFieldQuery.data.key]);
        return newPrev;
      });
    },
    1,
    [
      field,
      fieldKey,
      sectionKey,
      debouncedQueryFn,
      JSON.stringify(values),
      JSON.stringify(errors),
      dispatchStoreAction,
      setValues,
      setTouched,
      JSON.stringify(path),
    ]
  );

  return {
    retryFn,
  };
};
